'use client'

import { useLanguage, useTranslate } from '@repo/shared/hooks'
import { type FormSchema, useAppForm } from '@repo/ui/hooks'

import { useAddCardSchema } from '@repo/shared/schemas'
import { useAddShebaSchema } from '@repo/shared/schemas'
import { useCreateBank } from '@repo/services'
import {
  Row,
  BankCardField,
  FormField,
  Form,
  FormItemWrapper,
  Button,
  Checkbox,
  Typography,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  ModalTitle,
  ModalHeader,
  onCloseModal,
  InputNumber,
} from '@repo/ui/components'
import { useErrorHandler } from '@repo/shared/hooks'

export const CardInput = () => {
  const { t } = useTranslate()
  const language = useLanguage()
  const { addCardSchema } = useAddCardSchema()
  const { addShebaSchema } = useAddShebaSchema()
  const { errorHandler } = useErrorHandler()
  const { form: cardForm } = useAppForm({
    formSchema: addCardSchema,
    defaultValues: {
      addSheba: true,
    },
  })

  const { form: shebaForm } = useAppForm({
    formSchema: addShebaSchema,
    defaultValues: {},
  })
  const createBankMutation = useCreateBank({ invalidate: true })
  const onSubmit = async (
    data: FormSchema<typeof addCardSchema | typeof addShebaSchema>,
  ) => {
    cardForm.clearErrors()
    if ('cardNumber' in data) {
      createBankMutation.mutate(
        {
          type: 'card',
          value: data.cardNumber.replace(/-/g, ''),
          addIBAN: data.addSheba,
        },
        {
          onError: (error) => {
            const message = errorHandler(error, {
              type: 'both',
            })
            shebaForm.setError('cardNumber', {
              message,
            })
          },
          onSuccess: () => {
            cardForm.reset()
            onCloseModal()
          },
        },
      )
    } else {
      createBankMutation.mutate(
        { type: 'iban', value: `IR${data.shebaNumber}` },
        {
          onError: (error) => {
            const message = errorHandler(error, {
              type: 'both',
            })
            shebaForm.setError('shebaNumber', {
              message,
            })
          },
          onSuccess: () => {
            shebaForm.reset()
            onCloseModal()
          },
        },
      )
    }
  }

  return (
    <>
      <ModalHeader>
        <ModalTitle />
      </ModalHeader>
      <Tabs defaultValue='tab1' color='base' size='md' scrollable={false}>
        <TabsList>
          <TabsTrigger value='tab1'>
            {t('addCardPage_cardNumber_tab')}
          </TabsTrigger>
          <TabsTrigger value='tab2'>{t('addCardPage_sheba_tab')}</TabsTrigger>
        </TabsList>
        <TabsContent value='tab1'>
          <Form {...cardForm}>
            <form
              className='space-y-8 w-full '
              id='card-input-form'
              onSubmit={(e) => {
                e.stopPropagation()
                cardForm.handleSubmit(onSubmit)(e)
              }}
            >
              <FormField
                control={cardForm.control}
                name='cardNumber'
                render={({ field }) => (
                  <FormItemWrapper
                    title={t('addCardPage_cardNumber')}
                    caption={t('addCardPage_cardNumber_card_caption')}
                    inputSize='md'
                  >
                    <BankCardField {...field} />
                  </FormItemWrapper>
                )}
              />

              <FormField
                control={cardForm.control}
                name='addSheba'
                render={({ field }) => (
                  <FormItemWrapper inputSize='sm'>
                    <Row className='gap-2'>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id='addSheba'
                        size='sm'
                      />
                      <Typography
                        variant='label'
                        weight='regular'
                        size='sm'
                        htmlFor='addSheba'
                        color='text-neutral-surface-100'
                      >
                        {t('addCardPage_cardNumber_sheba_hint')}
                      </Typography>
                    </Row>
                  </FormItemWrapper>
                )}
              />

              <Button
                className='w-full'
                form='card-input-form'
                type='submit'
                variant='fill'
                size='lg'
                loading={createBankMutation.isPending}
              >
                {t('addCardPage_submit')}
              </Button>
            </form>
          </Form>
        </TabsContent>
        <TabsContent value='tab2'>
          <Form {...shebaForm}>
            <form
              onSubmit={(e) => {
                e.stopPropagation()
                shebaForm.handleSubmit(onSubmit)(e)
              }}
              className='space-y-8 w-full'
              id='sheba-input-form'
            >
              <FormField
                control={shebaForm.control}
                name='shebaNumber'
                render={({ field }) => (
                  <FormItemWrapper
                    title={t('addCardPage_shebaNumber')}
                    caption={t('addCardPage_cardNumber_sheba_caption')}
                    inputSize='md'
                  >
                    <InputNumber
                      startIcon={
                        language !== 'fa' ? (
                          <Typography
                            variant='label'
                            weight='regular'
                            size='sm'
                            color='text-neutral-surface-80'
                          >
                            IR
                          </Typography>
                        ) : undefined
                      }
                      endIcon={
                        language === 'fa' ? (
                          <Typography
                            variant='label'
                            weight='regular'
                            size='sm'
                            color='text-neutral-surface-80'
                          >
                            IR
                          </Typography>
                        ) : undefined
                      }
                      className='ltr'
                      type='text'
                      autoComplete='off'
                      {...field}
                      inputSize='md'
                      autoFocus
                    />
                  </FormItemWrapper>
                )}
              />

              <Button
                className='w-full'
                variant='fill'
                size='lg'
                form='sheba-input-form'
                type='submit'
                loading={createBankMutation.isPending}
              >
                {t('addCardPage_submit')}
              </Button>
            </form>
          </Form>
        </TabsContent>
      </Tabs>
    </>
  )
}
